import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Leaf, ChevronRight, Save, Loader2, LogOut } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TIER_ORDER = [25, 50, 100, 150];
const FIELDS = [
  { key: "digital", label: "Digital PDF (₹)", type: "number" },
  { key: "physical_softcover", label: "Softcover (₹)", type: "number" },
  { key: "physical_hardcover", label: "Hardcover (₹)", type: "number" },
  { key: "color_markup", label: "Color Markup (₹)", type: "number" },
  { key: "max_chars", label: "Max Characters", type: "number" },
  { key: "max_images", label: "Max Images", type: "number" },
];

const AdminPricing = () => {
  const navigate = useNavigate();
  const [tiers, setTiers] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [token, setToken] = useState(localStorage.getItem("admin_token") || "");

  useEffect(() => {
    if (!token) { navigate("/admin"); return; }
    fetchPricing();
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchPricing = async () => {
    try {
      const res = await axios.get(`${API}/admin/pricing`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTiers(res.data.tiers);
    } catch (err) {
      if (err.response?.status === 401) { toast.error("Admin session expired"); navigate("/admin"); }
      else toast.error("Failed to load pricing");
    }
    setLoading(false);
  };

  const handleChange = (tier, field, value) => {
    setTiers(prev => ({
      ...prev,
      [tier]: { ...prev[tier], [field]: Number(value) }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/pricing`, { tiers }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Pricing updated successfully!");
      await fetchPricing();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save pricing");
    }
    setSaving(false);
  };

  if (loading) return (<div className="min-h-screen bg-[#F8F6F1] flex items-center justify-center"><div className="w-10 h-10 border-3 border-[#7A9B7E] border-t-transparent rounded-full animate-spin" /></div>);

  return (
    <div className="min-h-screen bg-[#F8F6F1]" data-testid="admin-pricing-page">
      <nav className="sticky top-0 z-40 bg-[#F8F6F1]/80 backdrop-blur-xl border-b border-[#D4C9B8]/30">
        <div className="max-w-6xl mx-auto px-6 flex items-center justify-between h-14">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#9FB89F] to-[#7A9B7E] flex items-center justify-center"><Leaf className="w-4 h-4 text-white" /></div>
              <span className="font-serif text-lg font-medium text-[#2D3A2D]">My Story</span>
            </Link>
            <ChevronRight className="w-4 h-4 text-[#D4C9B8]" />
            <span className="font-sans text-sm text-[#C67B5C] font-medium">Admin — Pricing Manager</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/admin/dashboard" className="font-sans text-sm text-[#7A8A7A] hover:text-[#4A5D4A] transition-colors">Dashboard</Link>
            <button onClick={() => { localStorage.removeItem("admin_token"); navigate("/admin"); }} className="p-2 hover:bg-[#E5D4C1]/50 rounded-full"><LogOut className="w-4 h-4 text-[#7A8A7A]" /></button>
          </div>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-serif text-3xl font-medium text-[#2D3A2D]" data-testid="admin-pricing-title">Pricing Matrix</h1>
            <p className="font-sans text-sm text-[#7A8A7A] mt-1">Edit prices for all tiers. Changes take effect immediately.</p>
          </div>
          <button onClick={handleSave} disabled={saving} className="flex items-center gap-2 px-6 py-3 bg-[#4A5D4A] text-white rounded-xl font-sans text-sm font-medium hover:bg-[#2D3A2D] transition-colors disabled:opacity-60" data-testid="save-pricing-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saving ? "Saving..." : "Save All"}
          </button>
        </div>

        {/* Matrix Table */}
        <div className="bg-white/80 border border-[#D4C9B8]/40 rounded-2xl overflow-hidden" data-testid="pricing-matrix">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#D4C9B8]/30">
                  <th className="text-left px-6 py-4 font-sans text-xs text-[#7A8A7A] uppercase tracking-wider">Field</th>
                  {TIER_ORDER.map(tier => (
                    <th key={tier} className="text-center px-6 py-4 font-sans text-xs text-[#7A8A7A] uppercase tracking-wider">{tier} Pages</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {FIELDS.map((field, i) => (
                  <tr key={field.key} className={i % 2 === 0 ? "bg-[#F8F6F1]/50" : ""}>
                    <td className="px-6 py-3 font-sans text-sm text-[#2D3A2D] font-medium">{field.label}</td>
                    {TIER_ORDER.map(tier => (
                      <td key={tier} className="px-4 py-3 text-center">
                        <input
                          type="number"
                          value={tiers[String(tier)]?.[field.key] ?? ""}
                          onChange={(e) => handleChange(String(tier), field.key, e.target.value)}
                          className="w-full max-w-[120px] mx-auto px-3 py-2 rounded-lg bg-white border border-[#D4C9B8] font-sans text-sm text-center outline-none focus:border-[#7A9B7E] focus:ring-1 focus:ring-[#7A9B7E]/30"
                          data-testid={`price-${tier}-${field.key}`}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <p className="font-sans text-xs text-[#9A9A8A] mt-4 text-center">All prices in INR (₹). The checkout and pricing pages pull from this table dynamically.</p>
      </div>
    </div>
  );
};

export default AdminPricing;
